// Portfel — wspólne dane i obliczenia modułu: konta, kategorie, podsumowanie
// miesiąca finansowego („Do wydania") i wystąpienia planów. Kwoty w groszach,
// ze znakiem: ujemne to wydatek, dodatnie to wpływ.
import { computed, ref } from 'vue';
import { useApi } from '~/composables/useApi.ts';
import { money, ymd } from '@/composables/useV2Crud.js';
import { intlLocale, language } from '@/composables/useLanguage.js';
import { t } from '@/i18n/index.js';

// Wspólny stan na cały moduł — przejście między zakładkami nie pobiera słowników od nowa.
const accounts = ref([]);
const categories = ref([]);
const started = ref(null);

export const ACCOUNT_TYPES = [
    { value: 'bank', get label() { return t('wallet.accountTypes.bank'); }, icon: 'pi-building-columns' },
    { value: 'credit_card', get label() { return t('wallet.accountTypes.creditCard'); }, icon: 'pi-credit-card' },
    { value: 'cash', get label() { return t('wallet.accountTypes.cash'); }, icon: 'pi-money-bill' },
    { value: 'savings', get label() { return t('wallet.accountTypes.savings'); }, icon: 'pi-gift' },
];
export const accountIcon = (type) => ACCOUNT_TYPES.find(a => a.value === type)?.icon ?? 'pi-wallet';

export const INTERVALS = [
    { value: 'monthly', get label() { return t('wallet.intervals.monthly'); } },
    { value: 'quarterly', get label() { return t('wallet.intervals.quarterly'); } },
    { value: 'yearly', get label() { return t('wallet.intervals.yearly'); } },
];

// „−45,00 zł" / „+8 000,00 zł" — znak zawsze widoczny, minus typograficzny.
export const signed = (grosze) => {
    if (grosze == null) return '—';
    if (grosze === 0) return money(0);
    return `${grosze < 0 ? '−' : '+'}${money(Math.abs(grosze))}`;
};

// Kwota bez znaku — gdy znak mówi kolor albo nagłówek („Wydatki 1 200 zł").
export const abs = (grosze) => money(grosze == null ? null : Math.abs(grosze));

// Tekst z pola kwoty („45", „45,50", „1 200.5") → grosze, bez znaku; null = puste albo niepoprawne.
export const parseZl = (text) => {
    const clean = String(text ?? '').replace(/\s/g, '').replace(',', '.');
    if (!clean) return null;
    const n = Number(clean);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
};

export const todayYmd = () => ymd(new Date());
export const dayShift = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return ymd(d); };

// „dziś", „wczoraj", „pt 16 paź" — krótko, do list i chipów.
export const shortDay = (value) => {
    const key = String(value).slice(0, 10);
    if (key === todayYmd()) return t('wallet.days.today');
    if (key === dayShift(-1)) return t('wallet.days.yesterday');
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(intlLocale(), { weekday: 'short', day: 'numeric', month: 'short' });
};

// Nagłówek dnia na liście transakcji: „Dziś", „Wczoraj", „Poniedziałek, 12 października".
export const dayHeading = (key) => {
    if (key === todayYmd()) return t('wallet.days.todayHeading');
    if (key === dayShift(-1)) return t('wallet.days.yesterdayHeading');
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(intlLocale(), { weekday: 'long', day: 'numeric', month: 'long' });
};

// Miesiąc finansowy „2026-10" → „październik 2026".
export const monthLabel = (label, opts = { month: 'long', year: 'numeric' }) => {
    const [y, m] = label.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(intlLocale(), opts);
};
// „do marca 2027" — `toLocaleDateString` daje mianownik, a „do" rządzi dopełniaczem.
const GENITIVE = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'];
export const untilMonth = (label) => {
    const [y, m] = label.split('-').map(Number);
    const month = language.value === 'pl'
        ? GENITIVE[m - 1]
        : new Date(y, m - 1, 1).toLocaleDateString(intlLocale(), { month: 'long' });
    return t('wallet.days.untilMonth', { month, year: y });
};
export const shiftMonth = (label, n) => {
    const [y, m] = label.split('-').map(Number);
    const d = new Date(y, m - 1 + n, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
export const currentCalendarMonth = () => todayYmd().slice(0, 7);

// Termin wystąpienia planu względem dziś: „po terminie 3 dni", „dziś", „za 5 dni", „15 lis".
export const dueText = (occurrence) => {
    if (occurrence.status === 'paid') return t('wallet.due.paid');
    if (occurrence.status === 'skipped') return t('wallet.due.skipped');
    const [y, m, d] = occurrence.due_date.split('-').map(Number);
    const due = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = Math.round((due - today) / 86400000);
    if (days < 0) return t('wallet.due.overdue', { n: -days }, -days);
    if (days === 0) return t('wallet.due.today');
    if (days === 1) return t('wallet.due.tomorrow');
    if (days <= 7) return t('wallet.due.inDays', { n: days });
    return due.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short' });
};

// Nazwa kategorii transakcji albo jej podziału („Jedzenie + 2").
export const categoryText = (t) => {
    if (t.category) return t.category.name;
    if (t.splits?.length) return `${t.splits[0].category.name} + ${t.splits.length - 1}`;
    return '—';
};

// Pierwsza linia wiersza transakcji: kontrahent, notatka albo kategoria.
export const transactionTitle = (t) => t.counterparty || t.note || categoryText(t);
// Druga linia: kategoria — chyba że już jest tytułem.
export const transactionSubtitle = (t) => (t.counterparty || t.note ? categoryText(t) : null);

// Ostatnio używane kategorie (na chipy w szybkim dodawaniu) — w przeglądarce,
// bo to tylko wygoda jednego urządzenia.
const RECENT_KEY = 'rountine_portfel_recent_categories';
export const recentCategories = () => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]'); } catch { return []; }
};
export const rememberCategory = (uuid) => {
    try {
        const list = [uuid, ...recentCategories().filter(u => u !== uuid)].slice(0, 12);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch { /* tryb prywatny */ }
};

// Zdjęcie paragonu przed wysłaniem: najwyżej 1600 px na dłuższym boku, JPEG ~80% —
// z kilku MB robi się ok. 250 KB, a tekst zostaje czytelny. PDF idzie bez zmian.
const MAX_SIDE = 1600;
export const compressImage = async (file) => {
    if (!file.type.startsWith('image/')) return file;
    try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close?.();
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
        if (!blob) return file;
        return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    } catch {
        // format, którego przeglądarka nie odczyta (np. HEIC na komputerze) — wysyłamy jak jest
        return file;
    }
};

// Zdjęcia do transakcji (już zapisanej) — zwraca wszystkie jej załączniki po wysłaniu.
export const uploadAttachments = async (api, transactionUuid, files) => {
    if (!files.length) return null;
    const form = new FormData();
    for (const f of await Promise.all(files.map(compressImage))) form.append('files[]', f, f.name);
    return api.post(`/wallet/transactions/${transactionUuid}/attachments`, form);
};

// Plik załącznika jako adres w przeglądarce — pliki są dostępne tylko z tokenem, więc
// pobieramy je jako blob; adresy trzymamy, żeby miniatury nie pobierały się co chwilę.
const fileUrls = new Map();
export const attachmentUrl = async (api, uuid) => {
    if (!fileUrls.has(uuid)) {
        const blob = await api.get(`/wallet/attachments/${uuid}`, { responseType: 'blob' });
        fileUrls.set(uuid, URL.createObjectURL(blob));
    }
    return fileUrls.get(uuid);
};

// Każdy zapis w Portfelu (POST/PUT/PATCH/DELETE przez `api` z usePortfel) powiadamia
// słuchaczy — tak Pierwsze kroki wiedzą, że mogą sprawdzić, czy zadanie już jest zrobione.
const changeListeners = new Set();
export const onWalletChange = (fn) => { changeListeners.add(fn); return () => changeListeners.delete(fn); };
export const walletChanged = () => changeListeners.forEach(fn => fn());
const notifying = (call) => (...args) => call(...args).then((res) => { walletChanged(); return res; });

export function usePortfel() {
    const raw = useApi();
    const api = { ...raw, post: notifying(raw.post), put: notifying(raw.put), patch: notifying(raw.patch), delete: notifying(raw.delete) };

    const loadDictionaries = async () => {
        const [a, c] = await Promise.all([api.get('/wallet/accounts'), api.get('/wallet/categories')]);
        accounts.value = a;
        categories.value = c;
        return { accounts: a, categories: c };
    };

    const loadStatus = async () => {
        const s = await api.get('/wallet');
        started.value = s.started;
        return s.started;
    };

    const activeAccounts = computed(() => accounts.value.filter(a => !a.archived));
    const defaultAccount = computed(() => accounts.value.find(a => a.is_default) ?? activeAccounts.value[0] ?? null);
    const expenseCategories = computed(() => categories.value.filter(c => c.kind === 'expense'));
    const incomeCategories = computed(() => categories.value.filter(c => c.kind === 'income'));
    const totalBalance = computed(() => activeAccounts.value.reduce((s, a) => s + (a.balance ?? 0), 0));

    return {
        api,
        accounts,
        categories,
        started,
        activeAccounts,
        defaultAccount,
        expenseCategories,
        incomeCategories,
        totalBalance,
        loadDictionaries,
        loadStatus,
    };
}
